import { FaPlus } from 'react-icons/fa6';
import { MdDelete } from 'react-icons/md';
import Input from 'components/Common/Input';
import Button from 'components/Common/Button';
import TelInput from 'components/Common/TelInput';
import { useFieldArray, useForm } from 'react-hook-form';
import { Typography, Box, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PasswordInput from 'components/Common/PasswordInput';
import { InboundSipTrunkResponse } from 'apis/business/agent/deployPhone/inboundSipTrunk/types';
import {
  useCreateInboundSipTrunk,
  useUpdateInboundSipTrunk
} from 'apis/business/agent/deployPhone/inboundSipTrunk/hook';
import { OutboundSipTrunkResponse } from 'apis/business/agent/deployPhone/outBoundSipTrunk/types';
import {
  useCreateOutboundSipTrunk,
  useUpdateOutboundSipTrunk
} from 'apis/business/agent/deployPhone/outBoundSipTrunk/hook';
import Radio from 'components/Common/Radio';
import {
  buildInboundTrunkPayloadFromSipRegistration,
  buildOutboundTrunkPayload,
  mapOutboundFormValues,
  OUTBOUND_SIP_DEFAULTS
} from './shared/outboundSipTrunkHelpers';
import OutboundSipTrunkAdditionalConfig from './shared/OutboundSipTrunkAdditionalConfig';

type ByocOutboundSipTrunkFormProps = {
  selectedProvider: string;
  outboundFormData: OutboundSipTrunkResponse | undefined;
  inboundFormData?: InboundSipTrunkResponse;
  telephonyProviderId?: number;
  isSipRegistration?: boolean;
  /** When set, skips agent SIP APIs and calls this instead (e.g. business Telephony drawer). */
  persist?: (payload: OutboundSipTrunkResponse) => Promise<void>;
};

export default function ByocOutboundSipTrunkForm({
  selectedProvider,
  outboundFormData,
  inboundFormData,
  telephonyProviderId,
  isSipRegistration = false,
  persist
}: ByocOutboundSipTrunkFormProps) {
  const queryClient = useQueryClient();
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();
  const isCustom = selectedProvider === 'custom';
  const isSipOrCustom = selectedProvider === 'sip_connectivity' || selectedProvider === 'custom';
  const { mutateAsync: createOutboundFormData, isPending: creatingOutboundSip } = useCreateOutboundSipTrunk();
  const { mutateAsync: updateOutboundFormData, isPending: updatingOutboundSip } = useUpdateOutboundSipTrunk();
  const { mutateAsync: createInboundFormData, isPending: creatingInboundSip } = useCreateInboundSipTrunk();
  const { mutateAsync: updateInboundFormData, isPending: updatingInboundSip } = useUpdateInboundSipTrunk();

  const { control, handleSubmit, watch } = useForm<OutboundSipTrunkResponse>({
    defaultValues: {
      ...OUTBOUND_SIP_DEFAULTS,
      additional_config: {
        ...OUTBOUND_SIP_DEFAULTS.additional_config,
        port: '5060'
      }
    },
    values: mapOutboundFormValues(outboundFormData)
  });

  const numberType = watch('additional_config.number_type');

  const {
    fields: customPhoneNumbers,
    append: phoneNumberFieldAppend,
    remove: phoneNumberFieldRemove
  } = useFieldArray({
    control,
    name: 'additional_config.numbers'
  });

  const onSubmit = async (data: OutboundSipTrunkResponse) => {
    const variant = {
      agentTelephony: 'byoc' as const,
      provider: selectedProvider,
      telephonyProviderId,
      isSipRegistration
    };
    const trunkBase = buildOutboundTrunkPayload(data, variant);

    if (persist) {
      await persist({ ...data, ...trunkBase, name: data.name });
      return;
    }

    if (isSipRegistration) {
      const inboundPayload = buildInboundTrunkPayloadFromSipRegistration(data, variant);

      if (outboundFormData?.id) {
        await updateOutboundFormData({ trunk: { id: data.id, ...trunkBase } });
      } else {
        await createOutboundFormData({ trunk: trunkBase });
      }

      if (inboundFormData?.id) {
        await updateInboundFormData({
          trunk: { id: inboundFormData.id, provider: selectedProvider, ...inboundPayload }
        });
      } else {
        await createInboundFormData({ trunk: { provider: selectedProvider, ...inboundPayload } });
      }

      await queryClient.invalidateQueries({ queryKey: ['agent', locationId, agentId] });
      await queryClient.invalidateQueries({ queryKey: ['gateway_state', locationId, agentId] });
      return;
    }

    if (outboundFormData?.id) {
      await updateOutboundFormData({ trunk: { id: data.id, ...trunkBase } });
    } else {
      await createOutboundFormData({ trunk: trunkBase });
    }
  };

  if (isSipRegistration) {
    return (
      <>
        <Grid container spacing={2} paddingY={3} paddingX={2} borderRadius={2}>
          <Grid size={12}>
            <Typography fontWeight={400} fontSize={15}>
              Number Type
            </Typography>
            <Radio
              name='additional_config.number_type'
              control={control}
              label=' '
              options={[
                { label: 'Phone', value: 'phone' },
                { label: 'Extension', value: 'extension' }
              ]}
            />
            {numberType === 'extension' ? (
              <>
                <Input
                  control={control}
                  fullWidth
                  sx={{ backgroundColor: 'white' }}
                  label='Extension'
                  name='additional_config.numbers[0].value'
                  placeholder='e.g. 121'
                  required
                  rules={{
                    required: 'Extension is required'
                  }}
                />
                <Typography fontSize='12px' color='text.secondary' mt={0.5} mb={2}>
                  The extension this SIP account belongs to.
                </Typography>
                <TelInput
                  control={control}
                  fullWidth
                  sx={{ backgroundColor: 'white' }}
                  label='Main Line Number'
                  name='additional_config.main_line_number'
                  placeholder='e.g. +14155550198'
                  defaultCountry='US'
                  preferredCountries={['US', 'CA']}
                  required
                  rules={{
                    required: 'Main Line Number is required'
                  }}
                />
                <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                  Enter the mainline number corresponding to the above extension.
                </Typography>
              </>
            ) : (
              <>
                <TelInput
                  control={control}
                  fullWidth
                  sx={{ backgroundColor: 'white' }}
                  label='Phone Number'
                  name='additional_config.numbers[0].value'
                  placeholder='e.g. +1234567890'
                  defaultCountry='US'
                  preferredCountries={['US', 'CA', 'IN']}
                  required
                  rules={{
                    required: 'Phone Number is required'
                  }}
                />
                <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                  The number this SIP account belongs to. Used as your caller ID when making calls.
                </Typography>
              </>
            )}
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              label='Friendly Name'
              name='name'
              sx={{ backgroundColor: 'white' }}
              placeholder='e.g. Linha Commercial'
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              Shown instead of the number in your lists. You can change it anytime.
            </Typography>
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              label='Username'
              name='additional_config.auth_username'
              sx={{ backgroundColor: 'white' }}
              required
              rules={{
                required: 'Username is required'
              }}
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              Your SIP account login — this is what identifies your device to the server.
            </Typography>
          </Grid>

          <Grid size={12}>
            <PasswordInput
              control={control}
              rules={{
                required: 'Password is required'
              }}
              fullWidth
              label='Password'
              name='additional_config.auth_password'
              sx={{ backgroundColor: 'white' }}
              required
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              The password for your SIP account.
            </Typography>
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              name='additional_config.authentication_username'
              sx={{ backgroundColor: 'white' }}
              label='Authentication Username'
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              Only needed if your provider gives you a separate ID for authentication, different from your regular
              username.
            </Typography>
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              name='additional_config.proxy'
              sx={{ backgroundColor: 'white' }}
              label='Proxy'
              placeholder='sip.provider.com:5060'
              required
              rules={{
                required: 'Proxy is required'
              }}
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              The main SIP server your app connects and registers to. This is the only field required to get connected.
            </Typography>
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              name='additional_config.outbound_proxy'
              sx={{ backgroundColor: 'white' }}
              label='Outbound Proxy'
              placeholder='sbc.provider.com:5060'
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              Forces all outgoing calls through this server instead of the default route. Leave blank unless your
              provider specifically requires it.
            </Typography>
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              name='additional_config.whitelisted_ip'
              sx={{ backgroundColor: 'white' }}
              label='Whitelisted IP'
              placeholder='e.g. 203.0.113.10'
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              We will use this IP to whitelist. If not provided, we will use the proxy to resolve the IP.
            </Typography>
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              name='additional_config.realm'
              sx={{ backgroundColor: 'white' }}
              label='Realm'
              placeholder='domain.com'
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              Used only during login/authentication to verify your credentials. Usually same as your Proxy address —
              leave blank to auto-detect.
            </Typography>
          </Grid>

          <Grid size={12}>
            <Input
              control={control}
              fullWidth
              sx={{ backgroundColor: 'white' }}
              label='From Domain'
              name='additional_config.from_domain'
              placeholder='domain.com'
            />
            <Typography fontSize='12px' color='text.secondary' mt={0.5}>
              The domain shown to the other party when you make a call. Usually same as your Proxy address — leave blank
              to auto-detect.
            </Typography>
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 3 }}>
          <Button
            isPending={creatingOutboundSip || updatingOutboundSip || creatingInboundSip || updatingInboundSip}
            variant='contained'
            color='primary'
            onClick={handleSubmit(onSubmit)}
            sx={{ textTransform: 'none' }}
          >
            {outboundFormData?.id || inboundFormData?.id ? 'Update Configuration' : 'Save Configuration'}
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <Grid container spacing={1} paddingY={3} paddingX={2} borderRadius={2}>
        {isCustom ? (
          <Grid size={12} marginBottom={0.5}>
            <Box
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              flexWrap={{ xs: 'wrap', sm: 'nowrap' }}
              gap={{ xs: 1, sm: 0 }}
            >
              <Typography fontWeight={400} fontSize={14}>
                Phone Numbers
              </Typography>
              <Button
                onClick={() => phoneNumberFieldAppend({ value: '' })}
                variant='text'
                sx={{
                  p: 0,
                  minWidth: 'unset',
                  fontSize: 14,
                  color: 'primary.main',
                  textTransform: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    backgroundColor: 'transparent'
                  }
                }}
              >
                <FaPlus style={{ marginRight: 4 }} />
                Add Number
              </Button>
            </Box>
            {customPhoneNumbers.map((field, index) => (
              <Box
                key={field.id}
                display='flex'
                justifyContent='space-between'
                alignItems='center'
                marginBottom={1}
                marginTop={2}
              >
                <TelInput
                  name={`additional_config.numbers[${index}].value`}
                  control={control}
                  placeholder='+1416676230'
                  defaultCountry='US'
                  label='Phone Numbers'
                  sx={{ backgroundColor: 'white', flex: 1, marginRight: 1 }}
                  fullWidth
                  required
                />
                {customPhoneNumbers.length > 1 && (
                  <IconButton size='small' color='error' onClick={() => phoneNumberFieldRemove(index)}>
                    <MdDelete />
                  </IconButton>
                )}
              </Box>
            ))}
          </Grid>
        ) : (
          <Grid size={12}>
            <TelInput
              name='additional_config.numbers[0].value'
              label='Phone Number'
              control={control}
              placeholder='+1416676230'
              defaultCountry='US'
              sx={{ backgroundColor: 'white' }}
              fullWidth
              required
            />
          </Grid>
        )}

        <Grid size={12} marginTop={1}>
          <Input
            control={control}
            fullWidth
            label='Friendly Name'
            name='name'
            sx={{ backgroundColor: 'white' }}
            placeholder='e.g. Linha Commercial'
          />
          <Typography fontSize='12px' color='text.secondary' mt={0.5}>
            Shown instead of the number in your lists. You can change it anytime.
          </Typography>
        </Grid>

        {isSipOrCustom ? (
          <>
            <Grid size={12} marginBottom={0.5} marginTop={1}>
              <Input
                control={control}
                fullWidth
                sx={{ backgroundColor: 'white' }}
                label='Hostname or IP'
                name='additional_config.address'
                required
              />
              <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                Hostname or IP the SIP INVITE is sent to. This is not a SIP URI and shouldn't contain the sip: protocol.
              </Typography>
            </Grid>
            <Grid size={12} my={2}>
              <Input
                control={control}
                fullWidth
                sx={{ backgroundColor: 'white' }}
                label='Designation Country'
                name='additional_config.destination_country'
                helperText='Two letter country code for the country the call terminates in. The country code is used to route calls.'
              />
            </Grid>
          </>
        ) : (
          <Grid size={12} my={2}>
            <Input
              control={control}
              fullWidth
              sx={{ backgroundColor: 'white' }}
              label='SIP Domain'
              name='additional_config.address'
              required
            />
          </Grid>
        )}

        <Grid size={12}>
          <Input
            control={control}
            fullWidth
            label='Username'
            name='additional_config.auth_username'
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>

        <Grid size={12} mt={2}>
          <PasswordInput
            control={control}
            fullWidth
            label='Password'
            name='additional_config.auth_password'
            sx={{ backgroundColor: 'white' }}
          />
        </Grid>

        {selectedProvider === 'ring_central' && (
          <>
            <Grid size={12}>
              <Typography fontWeight={400} fontSize={14} marginBottom={0.5} marginTop={1}>
                Outbound Proxy (Optional)
              </Typography>
              <Input
                control={control}
                fullWidth
                name='additional_config.outbound_proxy'
                sx={{ backgroundColor: 'white' }}
              />
            </Grid>
            <Grid size={12}>
              <Typography fontWeight={400} fontSize={14} marginBottom={0.5} marginTop={1}>
                Outbound Proxy Port (Optional)
              </Typography>
              <Input
                control={control}
                fullWidth
                name='additional_config.outbound_proxy_port'
                sx={{ backgroundColor: 'white' }}
              />
            </Grid>
            <Grid size={12}>
              <Typography fontWeight={400} fontSize={14} marginBottom={0.5} marginTop={1}>
                Whitelisted IP (Optional)
              </Typography>
              <Input
                control={control}
                fullWidth
                name='additional_config.whitelisted_ip'
                sx={{ backgroundColor: 'white' }}
                placeholder='e.g. 203.0.113.10'
              />
              <Typography fontSize='12px' color='text.secondary' mt={0.5}>
                We will use this IP to whitelist. If not provided, we will use the proxy to resolve the IP.
              </Typography>
            </Grid>
            <Grid size={12}>
              <Typography fontWeight={400} fontSize={14} marginBottom={0.5} marginTop={1}>
                Authentication Username (Optional)
              </Typography>
              <Input
                control={control}
                fullWidth
                name='additional_config.authentication_username'
                sx={{ backgroundColor: 'white' }}
              />
            </Grid>
            <Grid size={12}>
              <Typography fontWeight={400} fontSize={14} marginBottom={0.5} marginTop={1}>
                Auth Username (Optional)
              </Typography>
              <Input
                control={control}
                fullWidth
                name='additional_config.auth_username'
                sx={{ backgroundColor: 'white' }}
              />
            </Grid>
          </>
        )}

        {isSipOrCustom && (
          <OutboundSipTrunkAdditionalConfig
            control={control}
            showOptionalAuthUsername
            borderTop
            showWhitelistedIp={selectedProvider === 'sip_connectivity'}
          />
        )}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 1 }}>
        <Button
          isPending={creatingOutboundSip || updatingOutboundSip}
          variant='contained'
          color='primary'
          onClick={handleSubmit(onSubmit)}
          sx={{ textTransform: 'none', borderRadius: 6, px: 3, fontWeight: 500 }}
        >
          {outboundFormData?.id && !persist ? 'Update Configuration' : 'Save Configuration'}
        </Button>
      </Box>
    </>
  );
}
